import api from './axiosConfig';

// ─── Enums ────────────────────────────────────────────────────────────────────

export type CashbackType = 'Fixed' | 'Percentage';
export type UserIdType = 'Email' | 'OisterUserId';

// ─── View Models (responses from backend) ─────────────────────────────────────

export interface SpecialPartnerViewModel {
  id: string;
  campaignName: string;
  contentfulBusinessId: string | null;
  cashbackType: CashbackType;
  totalCashback: number;
  userCashback: number;
  fixedAmount: number | null; // if set, all transactions use this amount (no amount column in CSV)
}

export interface UploadReportRowResult {
  userId: string;
  success: boolean;
  errorMessage: string | null; // e.g. "User not found", "User account terminated"
}

export interface UploadReportResult {
  totalRows: number;
  successCount: number;
  failedCount: number;
  rows: UploadReportRowResult[];
}

export interface CampaignTransactionViewModel {
  id: string;
  userId: string;
  transactionAmount: number;
  totalAssignedCashback: number;
  customerAssignedCashback: number;
  transactionDate: string;
  businessContentfulId: string;
}

export interface AllCampaignTransactionViewModel extends CampaignTransactionViewModel {
  campaignId: string;
  campaignName: string;
}

export interface AllCampaignTransactionsResponse {
  pageNumber: number;
  pageSize: number;
  total: number;
  data: AllCampaignTransactionViewModel[];
}

export interface OfflineMerchantViewModel {
  merchantName: string;
}

export interface CampaignTransactionsResponse {
  pageNumber: number;
  pageSize: number;
  total: number;
  data: CampaignTransactionViewModel[];
}

// ─── Commands (request bodies) ────────────────────────────────────────────────

export interface ListSpecialPartnersCommand {}

export interface CreateSpecialPartnerCommand {
  campaignName: string;
  contentfulBusinessId: string;
  cashbackType: CashbackType;
  totalCashback: number;  // amount in DKK (if Fixed) or percentage 0-100 (if Percentage)
  userCashback: number;   // amount in DKK (if Fixed) or percentage 0-100 (if Percentage)
  fixedAmount: number | null; // optional fixed transaction amount (Fixed cashback type only)
}

export interface UpdateSpecialPartnerCommand {
  id: string;
  campaignName: string;
  contentfulBusinessId: string;
  cashbackType: CashbackType;
  totalCashback: number;
  userCashback: number;
  fixedAmount: number | null;
}

export interface DeleteSpecialPartnerCommand {
  id: string;
}

// ─── API calls ────────────────────────────────────────────────────────────────

export const listSpecialPartners = async (): Promise<SpecialPartnerViewModel[]> => {
  const response = await api.post('/offline-campaigns/list', {});
  return response.data;
};

export const createSpecialPartner = async (
  command: CreateSpecialPartnerCommand
): Promise<SpecialPartnerViewModel> => {
  const response = await api.post('/offline-campaigns/create', command);
  return response.data;
};

export const updateSpecialPartner = async (
  command: UpdateSpecialPartnerCommand
): Promise<SpecialPartnerViewModel> => {
  const response = await api.post('/offline-campaigns/update', command);
  return response.data;
};

export const deleteSpecialPartner = async (
  command: DeleteSpecialPartnerCommand
): Promise<void> => {
  await api.post('/offline-campaigns/delete', command);
};

export const listOfflineMerchants = async (): Promise<OfflineMerchantViewModel[]> => {
  const response = await api.post('/offline-campaigns/merchants', {});
  return response.data;
};

export const listAllCampaignTransactions = async (
  pageNumber: number,
  pageSize: number,
  merchantName?: string
): Promise<AllCampaignTransactionsResponse> => {
  const response = await api.post('/offline-campaigns/all-transactions', {
    ...(merchantName ? { merchantName } : {}),
    pagination: { pageNumber, pageSize },
  });
  return response.data;
};

export const listCampaignTransactions = async (
  campaignId: string | null,
  pageNumber: number,
  pageSize: number
): Promise<CampaignTransactionsResponse> => {
  const response = await api.post('/offline-campaigns/transactions', {
    ...(campaignId ? { campaignId } : {}),
    pagination: { pageNumber, pageSize },
  });
  return response.data;
};

export const uploadSpecialPartnerReport = async (
  partnerId: string,
  userIdType: UserIdType,
  csvFile: File
): Promise<UploadReportResult> => {
  const formData = new FormData();
  formData.append('CsvFile', csvFile);
  formData.append('PartnerId', partnerId);
  formData.append('UserIdType', userIdType);
  const response = await api.post('/offline-campaigns/upload-report', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};
